import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { LiveLogContainer, LiveLogLimits, LiveLogLineFrame, LiveLogOptions } from "@k8s-dashboard/contracts";
import { useAnnounce } from "@/app/Announcer";
import { useT } from "@/i18n/I18nProvider";
import { num } from "@/lib/format";
import {
  liveLogExcerpt,
  liveLogUTF8Bytes,
  parseLiveLogAnsi,
  sliceLiveLogRuns,
  type LiveLogANSIResult,
  type LiveLogTextRun,
} from "./liveLogAnsi";
import type { LiveLogStatus } from "./useLiveLogs";

const MIN_ROW = 27;
const OVERSCAN = 12;
const MAX_DOM_ROWS = 64;
const QUERY_BYTES = 256;
const QUERY_DEBOUNCE_MS = 200;
const SCAN_SLICE_MS = 8;
const SCAN_SLICE_BYTES = 2 * 1024 * 1024;
const PARSER_CACHE_ROWS = 256;
const PARSER_CACHE_BYTES = 4 * 1024 * 1024;
const SEARCH_QUERY_MARKS = [
  "live-log-search-query-applied",
  "live-log-search-debounce-cancel",
  "live-log-search-query-calc-start",
  "live-log-search-query-calc-complete",
  "live-log-search-query-calc-cancel",
] as const;

type SearchQueryMark = (typeof SEARCH_QUERY_MARKS)[number];

function markSearchQuery(name: SearchQueryMark) {
  performance.clearMarks(name);
  performance.mark(name);
}

const STATUS_ID: Record<LiveLogStatus, string> = {
  idle: "common.live.status.idle",
  connecting: "common.live.status.connecting",
  streaming: "common.live.status.streaming",
  ended: "common.live.status.ended",
  error: "common.live.status.error",
};

type ToggleKey = "timestamps" | "previous" | "follow";

export function LiveLogControls({
  containers,
  selected,
  options,
  limits,
  status,
  terminating,
  previousAvailable,
  onChange,
  onStart,
  onStop,
  onClear,
  onDownload,
  downloadable,
}: {
  containers: LiveLogContainer[];
  selected: string;
  options: LiveLogOptions;
  limits?: LiveLogLimits;
  status: LiveLogStatus;
  terminating: boolean;
  previousAvailable: boolean;
  onChange: (next: LiveLogOptions) => void;
  onStart: (next: Partial<LiveLogOptions>) => void;
  onStop: () => void;
  onClear: () => void;
  onDownload: () => void;
  downloadable: boolean;
}) {
  const live = status === "connecting" || status === "streaming";
  const patch = (next: Partial<LiveLogOptions>) =>
    onChange({ ...options, ...next, container: next.container ?? selected });

  const toggles: Array<[ToggleKey, boolean, string, Partial<LiveLogOptions>]> = [
    ["timestamps", false, "각 줄에 kubelet timestamp를 붙입니다", { timestamps: !options.timestamps }],
    [
      "previous",
      !previousAvailable,
      previousAvailable ? "이전 인스턴스의 로그" : "재시작 이력이 없어 이전 로그가 없습니다",
      { previous: !options.previous, follow: false },
    ],
    [
      "follow",
      Boolean(options.previous),
      options.previous ? "previous와 follow는 함께 쓸 수 없습니다" : "스트림을 계속 열어 둡니다",
      { follow: !options.follow, previous: false },
    ],
  ];
  const numbers: Array<["tailLines" | "sinceSeconds", string, number, string]> = [
    ["tailLines", "tail 줄 수", limits?.tailLinesMax ?? 5000, ""],
    ["sinceSeconds", "최근 N초 (선택)", limits?.maxSinceSeconds ?? 86400, "전체"],
  ];

  return (
    <div className="row row--wrap" style={{ gap: "var(--space-5)", alignItems: "flex-end", padding: "var(--space-5)" }}>
      <label className="field">
        <span className="field__label">Container</span>
        <select value={selected} disabled={live} onChange={(event) => patch({ container: event.target.value, previous: false })}>
          {containers.map((container) => (
            <option key={container.name} value={container.name}>
              {container.name}{container.kind === "init" ? " (init)" : ""}
              {container.restarts > 0 ? ` · 재시작 ${container.restarts}` : ""}
            </option>
          ))}
        </select>
      </label>
      {numbers.map(([key, label, max, placeholder]) => (
        <label className="field" key={key}>
          <span className="field__label">{label}</span>
          <input
            type="number"
            min={1}
            max={max}
            value={options[key] ?? (placeholder ? "" : 500)}
            disabled={live}
            placeholder={placeholder}
            onChange={(event) => patch({ [key]: Number(event.target.value) || undefined })}
          />
        </label>
      ))}
      <div className="chips" role="group" aria-label="스트림 옵션">
        {toggles.map(([key, off, title, next]) => (
          <button key={key} type="button" className="chip" aria-pressed={Boolean(options[key])} disabled={live || off} title={title} onClick={() => patch(next)}>
            {key}
          </button>
        ))}
      </div>
      <div className="row row--wrap">
        {live ? (
          <button type="button" className="chip" onClick={onStop}>일시정지 (연결 끊기)</button>
        ) : (
          <button type="button" className="chip" onClick={() => onStart({ container: selected })} disabled={!selected}>
            {status === "idle" ? "불러오기" : "다시 연결"}
          </button>
        )}
        <button type="button" className="linkish" onClick={onClear} disabled={live}>지우기</button>
        <button type="button" className="linkish" onClick={onDownload} disabled={!downloadable}>마스킹된 로그 내려받기</button>
      </div>
      <p className="muted" style={{ flexBasis: "100%", margin: 0 }}>
        {terminating && "이 Pod는 종료 중입니다 · "}
        상한은 서버가 강제합니다 — 한 줄 {num((limits?.maxLineBytes ?? 0) / 1024)}KiB · 세션{" "}
        {num((limits?.sessionSeconds ?? 0) / 60)}분 · 전송 {num((limits?.maxResponseBytes ?? 0) / 1048576)}MiB
      </p>
    </div>
  );
}

function useLiteralSearch(lines: LiveLogLineFrame[], announce: ReturnType<typeof useAnnounce>, messages: { limit: string; evicted: string }) {
  const [input, setInputState] = useState("");
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<number[]>([]);
  const [selected, setSelected] = useState<number>();
  const selectedRef = useRef<number>();
  const [navigation, setNavigation] = useState(0);
  const [completion, setCompletion] = useState(0);
  const [error, setError] = useState("");
  const generation = useRef(0);
  const applyingQuery = useRef("");
  const scanned = useRef(new Set<number>());
  const matched = useRef(new Set<number>());
  const lastQuery = useRef("");

  const setInput = useCallback((next: string) => {
    if (liveLogUTF8Bytes(next) > QUERY_BYTES) {
      setError(messages.limit);
      announce("live-log-search", messages.limit);
      return;
    }
    performance.clearMarks("live-log-search-input");
    performance.clearMarks("live-log-search-stable");
    performance.clearMeasures("live-log-search-input-to-stable");
    performance.mark("live-log-search-input");
    setError("");
    setInputState(next);
    setQuery("");
    setMatches([]);
    setSelected(undefined);
    selectedRef.current = undefined;
  }, [announce, messages.limit]);

  useEffect(() => {
    let applied = false;
    const timer = window.setTimeout(() => {
      applied = true;
      markSearchQuery("live-log-search-query-applied");
      setQuery(input);
    }, QUERY_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      if (!applied && input) markSearchQuery("live-log-search-debounce-cancel");
    };
  }, [input]);

  useEffect(() => {
    const currentSeq = new Set(lines.map((line) => line.seq));
    const queryChanged = lastQuery.current !== query;
    if (queryChanged) {
      lastQuery.current = query;
      applyingQuery.current = query;
      scanned.current.clear();
      matched.current.clear();
      setMatches([]);
      setSelected(undefined);
      selectedRef.current = undefined;
    } else {
      for (const seq of scanned.current) if (!currentSeq.has(seq)) scanned.current.delete(seq);
      for (const seq of matched.current) if (!currentSeq.has(seq)) matched.current.delete(seq);
    }
    if (!query) return;

    const pending = lines.filter((line) => !scanned.current.has(line.seq));
    const myGeneration = ++generation.current;
    const appliesInput = applyingQuery.current === query;
    performance.clearMarks("live-log-search-calc-start");
    performance.clearMarks("live-log-search-calc-complete");
    performance.clearMeasures("live-log-search-calc");
    performance.mark("live-log-search-calc-start");
    if (appliesInput) markSearchQuery("live-log-search-query-calc-start");
    let cursor = 0;
    let timer: number | undefined;
    let completed = false;
    const run = () => {
      if (myGeneration !== generation.current) return;
      const started = performance.now();
      let bytes = 0;
      while (cursor < pending.length && performance.now() - started < SCAN_SLICE_MS && bytes < SCAN_SLICE_BYTES) {
        const line = pending[cursor++]!;
        const plain = parseLiveLogAnsi(line.maskedLine, 0).normalizedPlain;
        bytes += liveLogUTF8Bytes(plain);
        scanned.current.add(line.seq);
        if (plain.includes(query)) matched.current.add(line.seq);
      }
      if (cursor < pending.length) {
        timer = window.setTimeout(run, 0);
        return;
      }
      const ordered = lines.filter((line) => matched.current.has(line.seq)).map((line) => line.seq);
      setMatches(ordered);
      const current = selectedRef.current;
      const next = current !== undefined && ordered.includes(current) ? current : ordered[0];
      selectedRef.current = next;
      setSelected(next);
      if (next !== undefined && next !== current) setNavigation((value) => value + 1);
      completed = true;
      performance.mark("live-log-search-calc-complete");
      performance.measure("live-log-search-calc", "live-log-search-calc-start", "live-log-search-calc-complete");
      if (appliesInput) {
        applyingQuery.current = "";
        markSearchQuery("live-log-search-query-calc-complete");
        const position = next === undefined ? 0 : Math.max(0, ordered.indexOf(next)) + 1;
        announce("live-log-search-result", `${position} / ${ordered.length}`);
        setCompletion((value) => value + 1);
      }
    };
    run();
    return () => {
      generation.current++;
      if (timer !== undefined) window.clearTimeout(timer);
      if (!completed && appliesInput) markSearchQuery("live-log-search-query-calc-cancel");
    };
  }, [lines, query]);

  useEffect(() => () => {
    for (const name of SEARCH_QUERY_MARKS) performance.clearMarks(name);
  }, []);

  const move = useCallback((delta: number) => {
    if (matches.length === 0) return;
    const current = selectedRef.current;
    const index = current === undefined ? -1 : matches.indexOf(current);
    const next = matches[(index + delta + matches.length) % matches.length];
    selectedRef.current = next;
    setSelected(next);
    announce("live-log-search-result", `${matches.indexOf(next!) + 1} / ${matches.length}`);
    setNavigation((value) => value + 1);
  }, [announce, matches]);
  useEffect(() => {
    if (selected !== undefined && !lines.some((line) => line.seq === selected)) {
      announce("live-log-search", messages.evicted);
    }
  }, [announce, lines, messages.evicted, selected]);
  const current = selected === undefined ? 0 : Math.max(0, matches.indexOf(selected)) + 1;
  return { input, setInput, query, matches, selected, navigation, completion, current, error, previous: () => move(-1), next: () => move(1) };
}

function lowerBound(offsets: number[], value: number): number {
  let low = 0;
  let high = Math.max(0, offsets.length - 1);
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if ((offsets[middle + 1] ?? Number.POSITIVE_INFINITY) <= value) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** 현재 보관 창만 측정값을 소유합니다. seq가 같은 새 내용은 새 행이므로 높이를 재사용하지 않습니다. */
export function pruneLiveLogMeasurements(
  heights: Map<number, number>,
  sources: Map<number, string>,
  lines: readonly LiveLogLineFrame[],
): void {
  const current = new Set(lines.map((line) => line.seq));
  for (const seq of heights.keys()) if (!current.has(seq)) heights.delete(seq);
  for (const seq of sources.keys()) if (!current.has(seq)) sources.delete(seq);
  for (const line of lines) {
    const previous = sources.get(line.seq);
    if (previous !== undefined && previous !== line.maskedLine) heights.delete(line.seq);
    sources.set(line.seq, line.maskedLine);
  }
}

function renderTextRuns(runs: LiveLogTextRun[], query: string, selected: boolean) {
  let offset = 0;
  const matchStart = selected && query ? runs.map((run) => run.text).join("").indexOf(query) : -1;
  const matchEnd = matchStart < 0 ? -1 : matchStart + query.length;
  return runs.map((run, index) => {
    const start = offset;
    const end = start + run.text.length;
    offset = end;
    const className = [run.foreground ? `live-log-ansi--${run.foreground}` : "", run.bold ? "live-log-ansi--bold" : ""].filter(Boolean).join(" ");
    const overlapStart = Math.max(start, matchStart);
    const overlapEnd = Math.min(end, matchEnd);
    const body = overlapStart < overlapEnd ? (
      <>{run.text.slice(0, overlapStart - start)}<mark>{run.text.slice(overlapStart - start, overlapEnd - start)}</mark>{run.text.slice(overlapEnd - start)}</>
    ) : run.text;
    return className ? <span className={className} key={index}>{body}</span> : <Fragment key={index}>{body}</Fragment>;
  });
}

export function LiveLogViewport({ lines, dropped, window: windowSize, status, height = 420, sessionKey = "", onCopyLine }: {
  lines: LiveLogLineFrame[];
  dropped: number;
  window: number;
  status: LiveLogStatus;
  height?: number;
  sessionKey?: string;
  onCopyLine?: (text: string) => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [pinned, setPinned] = useState(true);
  const [wrap, setWrap] = useState(false);
  const [heightGeneration, setHeightGeneration] = useState(0);
  const pinnedRef = useRef(pinned);
  const pinnedFollowFrame = useRef<number>();
  const latestPinnedTarget = useRef(0);
  const heights = useRef(new Map<number, number>());
  const heightSources = useRef(new Map<number, string>());
  const anchor = useRef<{ seq: number; intra: number }>();
  const cache = useRef(new Map<number, { source: string; parsed: LiveLogANSIResult; bytes: number }>());
  const cacheBytes = useRef(0);
  const measuredSession = useRef(sessionKey);
  pinnedRef.current = pinned;
  const cancelPinnedFollow = useCallback(() => {
    if (pinnedFollowFrame.current !== undefined) cancelAnimationFrame(pinnedFollowFrame.current);
    pinnedFollowFrame.current = undefined;
  }, []);
  if (measuredSession.current !== sessionKey) {
    measuredSession.current = sessionKey;
    heights.current.clear();
    heightSources.current.clear();
    cache.current.clear();
    cacheBytes.current = 0;
  }
  pruneLiveLogMeasurements(heights.current, heightSources.current, lines);
  const announce = useAnnounce();
  const { t } = useT("common");
  const search = useLiteralSearch(lines, announce, {
    limit: t("common.live.search.limit", { bytes: QUERY_BYTES }),
    evicted: t("common.live.search.evicted"),
  });

  const parsedRow = useCallback((line: LiveLogLineFrame) => {
    const hit = cache.current.get(line.seq);
    if (hit?.source === line.maskedLine) {
      cache.current.delete(line.seq);
      cache.current.set(line.seq, hit);
      return hit.parsed;
    }
    if (hit) cacheBytes.current -= hit.bytes;
    const parsed = parseLiveLogAnsi(line.maskedLine);
    const bytes = liveLogUTF8Bytes(parsed.normalizedPlain);
    cache.current.set(line.seq, { source: line.maskedLine, parsed, bytes });
    cacheBytes.current += bytes;
    while (cache.current.size > PARSER_CACHE_ROWS || cacheBytes.current > PARSER_CACHE_BYTES) {
      const oldest = cache.current.entries().next().value as [number, { bytes: number }] | undefined;
      if (!oldest) break;
      cache.current.delete(oldest[0]);
      cacheBytes.current -= oldest[1].bytes;
    }
    return parsed;
  }, []);

  const offsets = useMemo(() => {
    void heightGeneration;
    const next = new Array<number>(lines.length + 1);
    next[0] = 0;
    for (let i = 0; i < lines.length; i++) next[i + 1] = next[i]! + (heights.current.get(lines[i]!.seq) ?? MIN_ROW);
    return next;
  }, [lines, heightGeneration]);
  const totalHeight = offsets.at(-1) ?? 0;
  latestPinnedTarget.current = lines.length ? totalHeight : 0;

  const onScroll = useCallback(() => {
    const element = viewportRef.current;
    if (!element) return;
    const actualScrollTop = element.scrollTop;
    const unchangedPosition = Math.abs(actualScrollTop - scrollTop) <= 0.5;
    const nativePinned = element.scrollHeight - actualScrollTop - element.clientHeight < MIN_ROW * 2;
    const preservesGeometryDrift = pinnedRef.current && pinnedFollowFrame.current !== undefined && unchangedPosition;
    const nextPinned = nativePinned || preservesGeometryDrift;
    setScrollTop(actualScrollTop);
    pinnedRef.current = nextPinned;
    if (!nextPinned) cancelPinnedFollow();
    setPinned(nextPinned);
    if (lines.length) {
      const index = lowerBound(offsets, actualScrollTop);
      const line = lines[index];
      if (line) anchor.current = { seq: line.seq, intra: actualScrollTop - offsets[index]! };
    }
  }, [cancelPinnedFollow, lines, offsets, scrollTop]);

  useLayoutEffect(() => {
    cancelPinnedFollow();
  }, [cancelPinnedFollow, sessionKey]);
  useLayoutEffect(() => () => cancelPinnedFollow(), [cancelPinnedFollow]);
  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!pinned || !element || lines.length === 0) {
      cancelPinnedFollow();
      return;
    }
    if (pinnedFollowFrame.current !== undefined) return;
    const scheduledElement = element;
    const scheduledSession = sessionKey;
    const frame = requestAnimationFrame(() => {
      if (pinnedFollowFrame.current !== frame) return;
      pinnedFollowFrame.current = undefined;
      if (!pinnedRef.current || measuredSession.current !== scheduledSession) return;
      if (viewportRef.current !== scheduledElement || !scheduledElement.isConnected) return;
      if (latestPinnedTarget.current <= 0) return;
      const nativeHeight = scheduledElement.scrollHeight;
      scheduledElement.scrollTop = nativeHeight;
    });
    pinnedFollowFrame.current = frame;
  }, [cancelPinnedFollow, lines.length, pinned, sessionKey, totalHeight]);
  useLayoutEffect(() => {
    if (pinned) return;
    const element = viewportRef.current;
    if (!element) return;
    const saved = anchor.current;
    if (!saved) return;
    const index = lines.findIndex((line) => line.seq === saved.seq);
    if (index >= 0) element.scrollTop = offsets[index]! + saved.intra;
  }, [lines, offsets, pinned]);

  useLayoutEffect(() => {
    const element = viewportRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const pending = new Map<number, number>();
    let frame: number | undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const seq = Number((entry.target as HTMLElement).dataset.seq);
        const legacyBox = entry.borderBoxSize as unknown as ResizeObserverSize | undefined;
        const borderBox = entry.borderBoxSize?.[0] ?? legacyBox;
        const blockSize = borderBox?.blockSize ?? (entry.target as HTMLElement).getBoundingClientRect().height;
        if (Number.isFinite(seq)) pending.set(seq, Math.max(MIN_ROW, blockSize));
      }
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        let changed = false;
        for (const [seq, measured] of pending) {
          if (heights.current.get(seq) !== measured) {
            heights.current.set(seq, measured);
            changed = true;
          }
        }
        pending.clear();
        if (changed) setHeightGeneration((value) => value + 1);
      });
    });
    element.querySelectorAll<HTMLElement>("[data-live-log-row]").forEach((row) => observer.observe(row));
    return () => {
      observer.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  });

  const invalidateHeights = useCallback(() => {
    heights.current.clear();
    setHeightGeneration((value) => value + 1);
  }, []);
  useEffect(invalidateHeights, [wrap, invalidateHeights]);
  useEffect(() => {
    const element = viewportRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    let width: number | undefined;
    const observer = new ResizeObserver((entries) => {
      const entry = entries.find((candidate) => candidate.target === element);
      if (!entry || !Number.isFinite(entry.contentRect.width)) return;
      const nextWidth = entry.contentRect.width;
      if (width === undefined) {
        width = nextWidth;
        return;
      }
      if (nextWidth !== width) {
        width = nextWidth;
        invalidateHeights();
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [invalidateHeights]);
  useEffect(() => {
    const observer = new MutationObserver(invalidateHeights);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class", "style"] });
    const fonts = document.fonts;
    fonts?.addEventListener("loadingdone", invalidateHeights);
    void fonts?.ready.then(invalidateHeights);
    return () => {
      observer.disconnect();
      fonts?.removeEventListener("loadingdone", invalidateHeights);
    };
  }, [invalidateHeights]);

  const latestLayout = useRef({ lines, offsets });
  latestLayout.current = { lines, offsets };
  const navigationFrame = useRef<number>();
  useLayoutEffect(() => {
    if (search.navigation === 0 || search.selected === undefined) return undefined;
    pinnedRef.current = false;
    cancelPinnedFollow();
    if (navigationFrame.current !== undefined) cancelAnimationFrame(navigationFrame.current);
    const element = viewportRef.current;
    const index = latestLayout.current.lines.findIndex((line) => line.seq === search.selected);
    if (!element || index < 0) return undefined;
    const estimated = latestLayout.current.offsets[index]!;
    anchor.current = { seq: search.selected, intra: 0 };
    element.scrollTop = estimated;
    setScrollTop(estimated);
    setPinned(false);
    const requested = search.navigation;
    navigationFrame.current = requestAnimationFrame(() => {
      navigationFrame.current = requestAnimationFrame(() => {
        navigationFrame.current = undefined;
        if (requested !== search.navigation) return;
        const current = latestLayout.current;
        const correctedIndex = current.lines.findIndex((line) => line.seq === search.selected);
        if (correctedIndex < 0 || !viewportRef.current) return;
        const corrected = current.offsets[correctedIndex]!;
        pinnedRef.current = false;
        cancelPinnedFollow();
        viewportRef.current.scrollTop = corrected;
        setScrollTop(corrected);
        setPinned(false);
      });
    });
    return () => {
      if (navigationFrame.current !== undefined) cancelAnimationFrame(navigationFrame.current);
      navigationFrame.current = undefined;
    };
    // Explicit selection navigation only. Row arrival/measurement must preserve manual scroll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.navigation]);

  const stableFrame = useRef<number>();
  useLayoutEffect(() => {
    if (search.completion === 0) return undefined;
    stableFrame.current = requestAnimationFrame(() => {
      stableFrame.current = requestAnimationFrame(() => {
        stableFrame.current = undefined;
        performance.mark("live-log-search-stable");
        performance.measure("live-log-search-input-to-stable", "live-log-search-input", "live-log-search-stable");
      });
    });
    return () => {
      if (stableFrame.current !== undefined) cancelAnimationFrame(stableFrame.current);
      stableFrame.current = undefined;
    };
  }, [search.completion]);

  const virtualScrollTop = pinned ? Math.max(0, totalHeight - height) : scrollTop;
  const first = Math.max(0, lowerBound(offsets, virtualScrollTop) - OVERSCAN);
  const wantedEnd = Math.min(lines.length, lowerBound(offsets, virtualScrollTop + height) + OVERSCAN + 1);
  const end = Math.min(wantedEnd, first + MAX_DOM_ROWS);
  const visible = lines.slice(first, end);
  const statusText = t(status === "streaming" && !pinned ? "common.live.status.unpinned" : STATUS_ID[status]);

  useEffect(() => {
    announce("live-logs", t("common.live.liveLogs", { status: statusText, lines: lines.length }));
  }, [announce, t, statusText, lines.length]);

  return (
    <div
      className="loglist"
      data-height-entries={heights.current.size}
      data-height-source-entries={heightSources.current.size}
      data-parser-cache-rows={cache.current.size}
      data-parser-cache-bytes={cacheBytes.current}
      onKeyDown={(event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "f") {
        event.preventDefault();
        searchRef.current?.focus();
      }
      }}
    >
      <div className="live-log-search" role="search" aria-label={t("common.live.search.label")}>
        <label className="field field--grow">
          <span className="field__label">{t("common.live.search.label")}</span>
          <input ref={searchRef} value={search.input} onChange={(event) => search.setInput(event.target.value)} placeholder={t("common.live.search.placeholder")} aria-describedby={search.error ? "live-log-search-error" : "live-log-search-scope"} />
        </label>
        <span id="live-log-search-scope" className="muted">{t("common.live.search.scope")}</span>
        <span className="num">{search.current} / {search.matches.length}</span>
        <button type="button" className="chip" disabled={!search.matches.length} onClick={search.previous}>{t("common.live.search.previous")}</button>
        <button type="button" className="chip" disabled={!search.matches.length} onClick={search.next}>{t("common.live.search.next")}</button>
        <button type="button" className="chip" aria-pressed={wrap} onClick={() => setWrap((value) => !value)}>{t("common.live.wrap")}</button>
        {search.error && <span id="live-log-search-error" className="live-log-search__error" role="alert">{search.error}</span>}
      </div>
      <div className="loglist__viewport" ref={viewportRef} onScroll={onScroll} style={{ height }} tabIndex={0} role="group" aria-label={t("common.live.viewport")}>
        <div style={{ height: totalHeight, position: "relative", minWidth: wrap ? "40rem" : undefined }}>
          {visible.map((line, visibleIndex) => {
            const index = first + visibleIndex;
            const parsed = parsedRow(line);
            const isSelected = line.seq === search.selected;
            const excerpt = liveLogExcerpt(parsed.normalizedPlain, search.query, isSelected);
            const runs = sliceLiveLogRuns(parsed.runs, excerpt.start, excerpt.end);
            return (
              <div key={line.seq} data-live-log-row data-seq={line.seq} className={`logline live-logline${isSelected ? " live-logline--selected" : ""}`} style={{ position: "absolute", top: offsets[index], left: 0, right: 0, minHeight: MIN_ROW }}>
                <div className={`logline__main live-logline__main${wrap ? " live-logline__main--wrap" : ""}`}>
                  <span className="logline__time">{line.timestamp ?? "—"}</span>
                  <span className="logline__msg live-logline__message">
                    {excerpt.prefixOmitted && (
                      <span className="live-logline__omission">
                        <span aria-hidden="true">…</span>
                        <span className="visually-hidden">{t("common.live.excerpt.omitted")}</span>
                      </span>
                    )}
                    {renderTextRuns(runs, search.query, isSelected)}
                    {excerpt.suffixOmitted && (
                      <span className="live-logline__omission">
                        <span aria-hidden="true">…</span>
                        <span className="visually-hidden">{t("common.live.excerpt.omitted")}</span>
                      </span>
                    )}
                  </span>
                  <span className="live-logline__badges">
                    {line.masked && <span className="logline__masked">마스킹됨</span>}
                    {line.truncated && <span className="logline__masked">잘림</span>}
                    <button type="button" className="linkish" onClick={() => onCopyLine?.(parsed.normalizedPlain)}>{t("common.live.copyRow")}</button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="loglist__footer">
        <span>보관 중인 줄 <span className="num">{num(lines.length)}</span> / 최대 <span className="num">{num(windowSize)}</span>{dropped > 0 && <> · 창을 넘겨 버린 줄 <span className="num">{num(dropped)}</span></>}</span>
        <span>{statusText}</span>
      </div>
    </div>
  );
}
